import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import Cookies from "js-cookie"

import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import getApiErrorMessages from "@utils/getApiErrorMessages"
import replaceSlugs from "@utils/replaceSlugs"

import toast from "@molecules/Toast/Toast"

import { CATALOG_ENDPOINTS } from "@endpoints/catalogs"

import {
    CatalogApiErrorResponse,
    CatalogBrandCount,
    CatalogBrandIdentity,
    CatalogDetails,
    CatalogResyncResponse,
    CatalogSubscriptionDetails,
    SubscribePayload,
    SubscribeResponse,
    SubscriptionPreviewCount,
} from "./SettingsCatalogs.types"

async function fetchCatalogJSON<ResponseData>(endpoint: string): Promise<ResponseData> {
    const response = await fetch(endpoint)

    if (!response.ok) {
        throw new Error(`Request to ${endpoint} failed with status ${response.status}.`)
    }

    return (await response.json()) as ResponseData
}

async function sendCatalogJSON<ResponseData>(
    endpoint: string,
    method: "POST" | "DELETE",
    payload?: unknown,
): Promise<ResponseData> {
    const response = await fetch(endpoint, {
        method,
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRFToken": Cookies.get("csrftoken") ?? "",
        },
        body: payload === undefined ? undefined : JSON.stringify(payload),
    })

    if (!response.ok) {
        throw (await response.json().catch(() => ({}))) as CatalogApiErrorResponse
    }

    if (response.status === 204) {
        return undefined as ResponseData
    }

    return (await response.json()) as ResponseData
}

export function useCatalogsQuery() {
    const { user } = useUser()
    const [catalogsEndpoint] = useSlugExtractor([CATALOG_ENDPOINTS.CATALOGS])

    return useQuery({
        queryKey: ["catalogs", catalogsEndpoint],
        queryFn: () => fetchCatalogJSON<CatalogDetails[]>(catalogsEndpoint),
        enabled: !!user?.service_company?.slug,
    })
}

export function useCatalogBrandsQuery(catalogId: number) {
    const { user } = useUser()
    const [brandsEndpointTemplate] = useSlugExtractor([CATALOG_ENDPOINTS.BRANDS])
    const brandsEndpoint = `${brandsEndpointTemplate}?catalog=${catalogId}`

    return useQuery({
        queryKey: ["catalog-brands", catalogId, brandsEndpoint],
        queryFn: () => fetchCatalogJSON<CatalogBrandIdentity[]>(brandsEndpoint),
        enabled: !!user?.service_company?.slug,
    })
}

export function useBrandCountQuery(catalogId: number) {
    const { user } = useUser()
    const [brandCountEndpointTemplate] = useSlugExtractor([CATALOG_ENDPOINTS.BRAND_COUNT])
    const brandCountEndpoint = `${brandCountEndpointTemplate}?catalog=${catalogId}`

    return useQuery({
        queryKey: ["catalog-brand-count", catalogId, brandCountEndpoint],
        queryFn: () => fetchCatalogJSON<CatalogBrandCount[]>(brandCountEndpoint),
        enabled: !!user?.service_company?.slug,
    })
}

export function useCatalogSubscriptionsQuery(catalogId: number) {
    const { user } = useUser()
    const [subscriptionsEndpointTemplate] = useSlugExtractor([CATALOG_ENDPOINTS.SUBSCRIPTIONS])
    const subscriptionsEndpoint = `${subscriptionsEndpointTemplate}?catalog=${catalogId}`

    return useQuery({
        queryKey: ["catalog-subscriptions", catalogId, subscriptionsEndpoint],
        queryFn: () => fetchCatalogJSON<CatalogSubscriptionDetails[]>(subscriptionsEndpoint),
        enabled: !!user?.service_company?.slug,
    })
}

// A subscription change never alters brand identities or item rows, only their derived coverage — so
// every subscribe/unsubscribe invalidates just the subscriptions listing and the per-brand counts that
// read off it, leaving the brand and item caches untouched.
export function useInvalidateSubscriptionState() {
    const queryClient = useQueryClient()

    return () => {
        void queryClient.invalidateQueries({ queryKey: ["catalog-subscriptions"] })
        void queryClient.invalidateQueries({ queryKey: ["catalog-brand-count"] })
    }
}

export function useSubscriptionPreviewCountMutation() {
    const [previewCountEndpoint] = useSlugExtractor([CATALOG_ENDPOINTS.SUBSCRIPTION_PREVIEW_COUNT])

    return useMutation<SubscriptionPreviewCount, CatalogApiErrorResponse, SubscribePayload>({
        mutationFn: (payload) => sendCatalogJSON<SubscriptionPreviewCount>(previewCountEndpoint, "POST", payload),
        onError: (error) => {
            const messages = getApiErrorMessages(error.non_field_errors)
            toast({ type: "error", title: messages[0] ?? "Could not preview the subscription." })
        },
    })
}

export function useSubscribeMutation() {
    const [subscriptionsEndpoint] = useSlugExtractor([CATALOG_ENDPOINTS.SUBSCRIPTIONS])
    const invalidateSubscriptionState = useInvalidateSubscriptionState()

    return useMutation<SubscribeResponse, CatalogApiErrorResponse, SubscribePayload>({
        mutationFn: (payload) => sendCatalogJSON<SubscribeResponse>(subscriptionsEndpoint, "POST", payload),
        onSuccess: (subscription) => {
            invalidateSubscriptionState()

            if (subscription.created) {
                toast({ type: "success", title: `Subscribed to ${subscription.scope_label}` })
            } else {
                toast({ type: "info", title: `Already covered by your ${subscription.scope_label} subscription` })
            }
        },
        onError: (error) => {
            const messages = getApiErrorMessages(error.non_field_errors)
            toast({ type: "error", title: messages[0] ?? "Could not subscribe." })
        },
    })
}

export function useUnsubscribeMutation() {
    const [subscriptionDetailsEndpointTemplate] = useSlugExtractor([CATALOG_ENDPOINTS.SUBSCRIPTION_DETAILS])
    const invalidateSubscriptionState = useInvalidateSubscriptionState()

    return useMutation<void, CatalogApiErrorResponse, CatalogSubscriptionDetails>({
        mutationFn: (subscription) =>
            sendCatalogJSON<void>(
                replaceSlugs(subscriptionDetailsEndpointTemplate, { id: String(subscription.id) }),
                "DELETE",
            ),
        onSuccess: (data, subscription) => {
            invalidateSubscriptionState()
            toast({ type: "success", title: `Unsubscribed from ${subscription.scope_label}` })
        },
        onError: (error) => {
            const messages = getApiErrorMessages(error.non_field_errors)
            toast({ type: "error", title: messages[0] ?? "Could not delete the subscription." })
        },
    })
}

export function useUnsubscribeFromCatalogMutation() {
    const [subscriptionsEndpoint] = useSlugExtractor([CATALOG_ENDPOINTS.SUBSCRIPTIONS])
    const [subscriptionDetailsEndpointTemplate] = useSlugExtractor([CATALOG_ENDPOINTS.SUBSCRIPTION_DETAILS])
    const invalidateSubscriptionState = useInvalidateSubscriptionState()

    return useMutation<void, CatalogApiErrorResponse, CatalogDetails>({
        mutationFn: async (catalog) => {
            const subscriptions = await fetchCatalogJSON<CatalogSubscriptionDetails[]>(
                `${subscriptionsEndpoint}?catalog=${catalog.id}&level=catalog`,
            )
            const catalogSubscription = subscriptions[0]

            if (!catalogSubscription) {
                return
            }

            await sendCatalogJSON<void>(
                replaceSlugs(subscriptionDetailsEndpointTemplate, { id: String(catalogSubscription.id) }),
                "DELETE",
            )
        },
        onSuccess: (data, catalog) => {
            invalidateSubscriptionState()
            toast({ type: "success", title: `Unsubscribed from ${catalog.name}` })
        },
        onError: (error) => {
            const messages = getApiErrorMessages(error.non_field_errors)
            toast({ type: "error", title: messages[0] ?? "Could not delete the subscription." })
        },
    })
}

export function useUnsubscribeFromBrandMutation() {
    const [subscriptionsEndpoint] = useSlugExtractor([CATALOG_ENDPOINTS.SUBSCRIPTIONS])
    const [subscriptionDetailsEndpointTemplate] = useSlugExtractor([CATALOG_ENDPOINTS.SUBSCRIPTION_DETAILS])
    const invalidateSubscriptionState = useInvalidateSubscriptionState()

    return useMutation<
        void,
        CatalogApiErrorResponse,
        { catalogId: number; brand: { id: number | null; name: string } }
    >({
        mutationFn: async ({ catalogId, brand }) => {
            const brandSubscriptions = await fetchCatalogJSON<CatalogSubscriptionDetails[]>(
                `${subscriptionsEndpoint}?catalog=${catalogId}&level=brand`,
            )
            const brandSubscription = brandSubscriptions.find((subscription) => subscription.brand === brand.id)

            if (!brandSubscription) {
                // The row can read as fully subscribed through a whole-catalog subscription rather than
                // its own brand row, so word the notice from whichever coverage is actually in force.
                const catalogSubscriptions = await fetchCatalogJSON<CatalogSubscriptionDetails[]>(
                    `${subscriptionsEndpoint}?catalog=${catalogId}&level=catalog`,
                )
                toast({
                    type: "info",
                    title: catalogSubscriptions.length
                        ? `${brand.name} is covered by your catalog subscription`
                        : `No brand subscription found for ${brand.name}`,
                })
                return
            }

            await sendCatalogJSON<void>(
                replaceSlugs(subscriptionDetailsEndpointTemplate, { id: String(brandSubscription.id) }),
                "DELETE",
            )
            toast({ type: "success", title: `Unsubscribed from ${brand.name}` })
        },
        onSuccess: () => {
            invalidateSubscriptionState()
        },
        onError: (error) => {
            const messages = getApiErrorMessages(error.non_field_errors)
            toast({ type: "error", title: messages[0] ?? "Could not delete the subscription." })
        },
    })
}

export function useCatalogResyncMutation() {
    const queryClient = useQueryClient()
    const [resyncEndpointTemplate] = useSlugExtractor([CATALOG_ENDPOINTS.CATALOG_RESYNC])

    return useMutation<CatalogResyncResponse, CatalogApiErrorResponse, CatalogDetails>({
        mutationFn: (catalog) =>
            sendCatalogJSON<CatalogResyncResponse>(
                replaceSlugs(resyncEndpointTemplate, { id: String(catalog.id) }),
                "POST",
            ),
        onSuccess: (resyncResult, catalog) => {
            // A sync can add or remove brands, categories, and items, so every catalog-shaped cache is stale;
            // the subscriptions listing is untouched by a resync and deliberately left alone.
            void queryClient.invalidateQueries({ queryKey: ["catalogs"] })
            void queryClient.invalidateQueries({ queryKey: ["catalog-brands"] })
            void queryClient.invalidateQueries({ queryKey: ["catalog-brand-count"] })
            void queryClient.invalidateQueries({ queryKey: ["catalog-categories"] })
            void queryClient.invalidateQueries({ queryKey: ["tableData"] })

            if (resyncResult.result === "started") {
                toast({ type: "success", title: `Syncing ${catalog.name}` })
            } else {
                toast({ type: "info", title: resyncResult.label })
            }
        },
        onError: (error) => {
            const messages = getApiErrorMessages(error.non_field_errors)
            toast({ type: "error", title: messages[0] ?? "Could not start the sync." })
        },
    })
}
